import { Block as BlockIcon, LockOutlined as LockOutlinedIcon } from 'components/icons';
import { ConfidentialChip } from 'components/matters/MatterIndicators';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Tooltip } from 'components/ui/tooltip';
import { DebriefingPersonRef } from 'pages/debriefing/types';
import { t } from 'utils/getTranslations';

/**
 * Restricted-row tooltip. `accessContacts` is composed by the BE per mode (case owners /
 * relationship owners / session leader) and is only populated on masked rows.
 */
export function restrictedAccessTooltip(accessContacts: DebriefingPersonRef[] | null | undefined): string {
    const names = (accessContacts ?? []).flatMap((contact) => (contact.name ? [contact.name] : []));
    return names.length > 0
        ? t('debriefing.restricted-contact', { names: names.join(', ') })
        : t('debriefing.restricted');
}

interface AccessIconProps {
    accessible: boolean;
    isConfidential: boolean | null | undefined;
    accessContacts?: DebriefingPersonRef[] | null;
}

/**
 * The debriefing's own markers, not the case's: slash-circle = restricted (masked row, tooltip names
 * who to contact), padlock = confidential but readable by the viewer — in the new design the record
 * page's "Confidential debriefing" chip instead. Nothing for ordinary rows.
 */
export function DebriefingAccessIcon({ accessible, isConfidential, accessContacts }: AccessIconProps) {
    const isNewDesign = useIsNewDesign();
    if (!accessible) {
        const label = restrictedAccessTooltip(accessContacts);
        return (
            <Tooltip content={label}>
                <BlockIcon
                    role="img"
                    aria-label={label}
                    aria-hidden={false}
                    className="text-fg-secondary h-3.5 w-3.5 shrink-0"
                />
            </Tooltip>
        );
    }
    if (isConfidential) {
        const label = t('debriefing.confidential');
        if (isNewDesign) {
            return <ConfidentialChip label={label} />;
        }
        return (
            <Tooltip content={label}>
                <LockOutlinedIcon
                    role="img"
                    aria-label={label}
                    aria-hidden={false}
                    className="text-fg-secondary h-3.5 w-3.5 shrink-0"
                />
            </Tooltip>
        );
    }
    return null;
}

/** Placeholder for cells the viewer may not see on a restricted row. */
export function MaskedCell() {
    return <span className="text-fg-secondary text-[13px]">{t('debriefing.masked-cell')}</span>;
}
